<script setup lang="ts">
// The machine's Monitoring tab (DECISIONS 2026-10-10 "monitoring locked on
// a node"): who watches the node, what, since when and by which route, what
// monitoring costs against its budget, and the lock: before an event an
// editor locks it, which cuts every viewer and refuses new ones until
// somebody unlocks it, each with a confirm.
import { computed, onMounted, ref } from 'vue'
import { central, nodeId } from '../../api'
import { coresText, lockText, sourceLabel, viewerRows } from '../../monitoring'
import { useNode } from '../../store'
import { ago } from '../../util'
import ConfirmDialog from '../ConfirmDialog.vue'
import Figure from './Figure.vue'

const node = useNode()
const st = computed(() => node.status?.monitor)
const cfg = computed(() => node.config ?? undefined)
// the lock as the document has it (the status says the same a second later)
const lock = computed(() => node.config?.monitor_lock ?? st.value?.lock)
// a relay is named "relay <node id>": its name from central's node list
const names = ref<Record<string, string>>({})
onMounted(() => {
    if (node.central && nodeId)
        void central
            .nodes()
            .then((ns) => (names.value = Object.fromEntries(ns.map((n) => [n.id, n.name]))))
            .catch(() => {})
})
const rows = computed(() => viewerRows(st.value, cfg.value, (id) => names.value[id] ?? id))
const sources = computed(() => (st.value?.sources ?? []).filter((s) => s.viewers > 0))
const share = computed(() =>
    st.value?.budget_cores ? Math.round((100 * st.value.cpu_cores) / st.value.budget_cores) : null,
)
const nodeName = computed(() => node.config?.node.name || 'this node')

const asking = ref<'lock' | 'unlock' | null>(null)
const busy = ref(false)
const confirmBody = computed(() => {
    if (asking.value === 'unlock') {
        const budget = st.value?.budget_cores ? `${+st.value.budget_cores.toFixed(1)} cores` : 'a quarter of its cores'
        return `Whoever may see ${nodeName.value} can watch it again: each picture takes some of its CPU (within its budget of ${budget}), at a lower priority than the encodes.`
    }
    const n = rows.value.length
    const cut = n ? `${n} viewer${n === 1 ? ' is' : 's are'} cut now and told why. ` : ''
    return `${cut}Nobody can watch ${nodeName.value} (walls, Live tabs, relays) until somebody unlocks it here; its monitor encodes stop, so their CPU goes back to the encodes on air.`
})
async function run() {
    const lockIt = asking.value === 'lock'
    asking.value = null
    busy.value = true
    try {
        await node.setMonitorLock(lockIt)
    } catch {
        /* toast shown by the store */
    } finally {
        busy.value = false
    }
}
</script>

<template>
    <div data-test="monitor-detail">
        <!-- the lock: what it is, who set it, and the one control -->
        <div
            class="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2.5 py-1.5"
            :class="lock ? 'border-warn/40 bg-warn/8' : 'border-line'"
            data-test="monitor-lock"
        >
            <b class="text-[13px] font-semibold" :class="lock ? 'text-warn' : ''">{{
                lock ? 'Monitoring locked' : 'Monitoring unlocked'
            }}</b>
            <span class="min-w-0 truncate text-xs text-fg-2">{{
                lock ? lockText(lock) + ': nobody can watch this node' : 'whoever may see this node can watch it'
            }}</span>
            <button
                v-if="!node.readOnly"
                class="ml-auto text-xs"
                :disabled="busy"
                :data-test="lock ? 'monitor-unlock' : 'monitor-lock-button'"
                @click="asking = lock ? 'unlock' : 'lock'"
            >
                {{ lock ? 'Unlock…' : 'Lock…' }}
            </button>
        </div>
        <p class="mt-1.5 text-xs text-fg-3">
            Monitor pictures run at a lower priority than the encodes, within a budget of cores; locking before an event
            stops them altogether and keeps them stopped until somebody unlocks it here.
        </p>

        <div class="mt-2 grid grid-cols-3 gap-x-3.5 gap-y-1.5">
            <Figure label="viewers"
                >{{ rows.length }}<template #sub>of {{ st?.max_viewers ?? '—' }} at most</template></Figure
            >
            <Figure label="cpu, monitoring" :full="coresText(st)"
                ><span :class="share !== null && share >= 90 ? 'text-warn' : ''">{{ coresText(st) }}</span
                ><template #sub>{{ share !== null ? `${share}% of its budget` : '' }}</template></Figure
            >
            <Figure label="sources watched"
                >{{ sources.length
                }}<template #sub>{{ st?.running ? 'gateway running' : 'gateway stopped' }}</template></Figure
            >
        </div>
        <div v-if="st?.error" class="mt-1 text-xs text-bad">{{ st.error }}</div>

        <div class="mt-2.5 mb-1 text-[11px] text-fg-3">who watches</div>
        <div v-if="!rows.length" class="text-xs text-fg-2">
            {{ lock ? 'Nobody: monitoring is locked.' : 'Nobody watches this node now.' }}
        </div>
        <table v-else class="w-full table-fixed text-xs" data-test="monitor-viewers">
            <thead class="text-left text-[11px] text-fg-3">
                <tr>
                    <th class="w-[34%] font-normal">who</th>
                    <th class="w-[30%] font-normal">watching</th>
                    <th class="w-[13%] font-normal">for</th>
                    <th class="w-[13%] font-normal">route</th>
                    <th class="w-[10%] text-right font-normal">rtt</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(r, i) in rows" :key="i" class="border-t border-line">
                    <td class="truncate py-0.5" :title="r.who">{{ r.who }}</td>
                    <td class="truncate" :title="r.source">{{ r.source }}</td>
                    <td class="truncate tabular-nums">{{ ago(r.since, node.now) }}</td>
                    <td class="truncate" :class="r.connected ? 'text-fg-2' : 'text-fg-3'">{{ r.route }}</td>
                    <td class="text-right text-fg-2 tabular-nums">{{ r.rtt }}</td>
                </tr>
            </tbody>
        </table>

        <template v-if="sources.length">
            <div class="mt-2.5 mb-1 text-[11px] text-fg-3">sources</div>
            <div v-for="s in sources" :key="s.source" class="flex gap-2 py-0.5 text-xs">
                <span class="min-w-0 flex-1 truncate" :title="s.source">{{ sourceLabel(cfg, s.source) }}</span>
                <span v-if="s.upstream" class="text-fg-2">from its origin: {{ s.upstream }}</span>
                <span class="w-20 text-right text-fg-2 tabular-nums"
                    >{{ s.viewers }} viewer{{ s.viewers === 1 ? '' : 's' }}</span
                >
            </div>
        </template>

        <ConfirmDialog
            v-if="asking"
            :title="asking === 'lock' ? `Lock monitoring on ${nodeName}?` : `Unlock monitoring on ${nodeName}?`"
            :body="confirmBody"
            :confirm-label="asking === 'lock' ? 'Lock monitoring' : 'Unlock monitoring'"
            @confirm="run"
            @cancel="asking = null"
        />
    </div>
</template>
